import React, {useEffect, useRef, useState} from 'react';
import {
  Animated,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';

import {control, field, radius, space, type, universal} from '../../design-system/tokens';
import theme from '../../style/theme';

import ChevronRightImg from './assets/ChevronRight.svg';
import MobileCartImg from './assets/MobileCart.svg';
import type {MenuAction} from './menu/menuTypes';
import type {MegaMenusResult} from './menu/useMegaMenus';
import {
  ACCOUNT_ICON_SIZE,
  AccountMenu,
  AccountMenuProps,
  AVATAR_SIZE,
  CHIP_RADIUS,
} from './AccountMenu';
import {BackToStoreButton} from './BackToStoreButton';
import {PAGE_MAX_WIDTH, useBand} from './breakpoints';
import {chromeType} from './chromeType';
import {headerChrome} from './headerChrome';
import {HeaderDisabledEntry, isEntryDenied} from './headerDenyList';
import {heightsFor} from './headerGeometry';
import {DESKTOP_LOGO_HEIGHT, HeaderLogo} from './HeaderLogo';
import {HeaderSearch} from './HeaderSearch';
import {
  Availability,
  AVAILABILITY_COPY,
  GIFT_CARD_STORE_COPY,
  HeaderActions,
  HeaderState,
  isCompactState,
  isEcomState,
  NO_STORE_COPY,
} from './headerTypes';
import {MegaMenuPanel} from './MegaMenuPanel';
import {hiddenWhen, useProgress} from './motion';

export type DesktopHeaderProps = {
  state: HeaderState;
  storeName?: string;
  availability: Availability;
  cartCount: number;
  /** Formatted, e.g. "$77.00" (Task A10 formats it). */
  cartTotal: string;
  showCart: boolean;
  /** False hides search, account and cart. */
  showUtilities: boolean;
  /** Gift-card store (R42): plain-text store selector, logo not pressable. */
  giftCardStore: boolean;
  /** False removes the store selector (R37: blinker flows). The 56 row keeps its height. */
  showStoreSelector: boolean;
  /** False removes the search field (routes whose header config sets `search: false`). */
  showSearch: boolean;
  denied: readonly HeaderDisabledEntry[];
  account: AccountMenuProps;
  actions: HeaderActions;
  /** Resolved mega menus from BrandHeader's `useMegaMenus` (C3): the triggers and panels. */
  menus: MegaMenusResult;
  /** A live mega menu link was pressed; the panel has closed. */
  onMenuAction(a: MenuAction): void;
  /** Forces a category menu open (stories). */
  openMenuId?: string;
};

// SHORTCUT: 150 ms hover intent is a prototype choice; no motion token exists. Upgrade path:
// a DIS-1735 motion token, or the delay design gives for opening a menu on hover.
const HOVER_INTENT_MS = 150;

// SHORTCUT: row height 56 is off the space scale. Upgrade path: layout.desktop header
// tokens once DIS-1735 adds them. Read from headerGeometry so the two cannot disagree.
const ROW_HEIGHT = heightsFor('desktop', 'nonEcom').full;
// SHORTCUT: Figma draws the mega menu panels at 1440 only (header/dropdown/desk 204:27672). A
// tall panel overran a short window (gate B D2: Wine ended at y 1195 at 1024 × 900), so the panel
// is capped at the window height below the header and scrolls inside. A panel shows only with the
// nav row, so the header is the full ecommerce one. Assumption: the header sits at the window top,
// as in the app. Ceiling: no design for short windows. Upgrade path: a designer frame for short
// viewports.
const panelMaxHeight = (windowHeight: number): number =>
  windowHeight - heightsFor('desktop', 'ecom').full;
// SHORTCUT: backdrop black at 30 % is Figma's overlay fill (header/dropdown/desk overlay
// 441:33782, rgba(0,0,0,0.3)); no overlay or opacity token exists. Ceiling: two overlay values
// live as literals, this one and MobileMenuSheet's 50 % scrim (no Figma source). Upgrade path:
// DIS-1735 adds an overlay token and both read it.
const BACKDROP_OPACITY = 0.3;
// SHORTCUT: search height 36 and width 535 are Figma layout literals with no token
// (header/desk 196:26065). Upgrade path: layout.desktop header tokens once DIS-1735 adds them.
const SEARCH_HEIGHT = 36;
const SEARCH_FIELD_WIDTH = 535;
// SHORTCUT: nav item gap 28 is off-scale (verdict "design error: off-scale; nearest
// space.s24 / space.s32"). Upgrade path: design picks s24 or s32.
const NAV_GAP = 28;
// SHORTCUT: icon sizes are Figma literals with no icon-size token: cart 23.333 × 20 (62:1633),
// chevron 16 (104:7822), menu 24 (the mobile header's, header/mob 65:4145).
// Upgrade path: a DIS-1735 icon-size scale. Each SVG call site repeats the file's own
// viewBox, which the web svgr config strips.
// DIS-2043: Figma draws the same cart as the mobile header at 1/1.4 scale, so MobileCart.svg is
// reused at its own viewBox.
const CART_ICON_WIDTH = 23.333;
const CART_ICON_HEIGHT = 20;
const CART_ICON_VIEWBOX = '0 0 32.667 28';
// SHORTCUT: badge height 11.667 and top 10 are off the `space` scale. Figma 62:1633 draws the
// 229:6121 badge 8.333 tall with 6.67 text; it was enlarged on request (DIS-2043) to the mobile
// header's badge size, about the Figma badge's centre. Upgrade path: a DIS-1735 badge token.
const BADGE_HEIGHT = 11.667;
const BADGE_TOP = 10;
const CHEVRON_SIZE = 16;
/** Account chip width plus the row gap: how far the store selector moves when it hides. */
const accountSlot = ({isLoggedIn, initials}: AccountMenuProps) =>
  (isLoggedIn && initials ? AVATAR_SIZE : ACCOUNT_ICON_SIZE) + 2 * space.s8 + space.s16;
/** On web a host ref is a DOM element; elsewhere it has no `contains`. */
const containsTarget = (node: unknown, target: unknown): boolean =>
  typeof (node as {contains?: unknown} | null)?.contains === 'function' &&
  (node as {contains(t: unknown): boolean}).contains(target);

/**
 * Desktop-class header (tablet, desktop, wide): top row (logo, search, store, account, cart)
 * and the nav row of menu triggers with the open menu's panel. Ported from the header
 * prototype (ea875fae6 DesktopHeader.tsx) without utility popovers.
 */
export const DesktopHeader = ({
  state,
  storeName,
  availability,
  cartCount,
  cartTotal,
  showCart,
  showUtilities,
  giftCardStore,
  showStoreSelector,
  showSearch,
  denied,
  account,
  actions,
  menus,
  onMenuAction,
  openMenuId,
}: DesktopHeaderProps) => {
  // `by` records what opened the menu: a press on a hover-opened trigger keeps it open
  // (ruling R24); only a press on a press-opened trigger closes it.
  const [opened, setOpened] = useState<{id: string; by: 'hover' | 'press'} | undefined>();
  const openMenu = opened?.id;
  // Two timers: a trigger's pending hover open, and the hover region's pending close. A pointer
  // that re-enters the region cancels only the close, so it never drops a trigger's open.
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // One ref per nav trigger (focus return, outside press) and one for the open panel.
  const triggerRefs = useRef(new Map<string, View>());
  const panelRef = useRef<View>(null);
  const backdropRef = useRef<View>(null);
  // The account panel is controlled here so the backdrop can show under it and close it.
  const [accountOpen, setAccountOpen] = useState(false);
  // HeaderSearch reports its panel; the backdrop also dims the page under it (Figma 161:17195).
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => setOpened(openMenuId ? {id: openMenuId, by: 'press'} : undefined), [openMenuId]);
  const clearTimers = () => {
    clearTimeout(timer.current);
    clearTimeout(closeTimer.current);
  };
  useEffect(() => clearTimers, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || openMenu === undefined) {
      return;
    }
    const onKeyDown = (event: {key?: string}) => {
      if (event.key === 'Escape') {
        // A trigger's pending hover open would reopen a menu 150 ms after Escape.
        clearTimers();
        setOpened(undefined);
        // The panel unmounts with focus inside it; focus returns to its trigger.
        triggerRefs.current.get(openMenu)?.focus();
      }
    };
    // A press anywhere but the panel and the nav triggers closes the open panel. A trigger
    // press is left to the trigger's own onPress, a backdrop press to the backdrop's: closing
    // on pointerdown would unmount the backdrop and hand the click to the page below it.
    const onPointerDown = (event: {target?: unknown}) => {
      const nodes = [panelRef.current, backdropRef.current, ...triggerRefs.current.values()];
      if (!nodes.some(node => containsTarget(node, event.target))) {
        clearTimers();
        setOpened(undefined);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [openMenu]);

  const afterIntent = (action: () => void) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(action, HOVER_INTENT_MS);
  };

  // header/tab 104:7864: the search fills the gap between logo and store (337 at 1023)
  // instead of sitting centred on the page; wide (371:33127) is desktop centred in 1920.
  const tablet = useBand() === 'tablet';
  const windowHeight = useWindowDimensions().height;
  const ecom = isEcomState(state);
  const navShown = ecom && !isCompactState(state);
  const accountShown = state !== 'noEcomCollapsed';
  const navProgress = useProgress(navShown);
  const accountProgress = useProgress(accountShown);
  // `navMenus`, not `menus`: the prop holds the resolver result (C3). `denied` still applies,
  // so the deny-list stories drive the row with one resolved `menus`.
  const navMenus = menus.data.filter(menu => !isEntryDenied(menu.flag, denied));
  const shownMenu = navMenus.find(menu => menu.id === openMenu);

  // Minimal compact hides the account chip; an open account panel (and its backdrop) closes.
  useEffect(() => {
    if (!accountShown) {
      setAccountOpen(false);
    }
  }, [accountShown]);

  // C5: collapsing to compact closes the open menu and drops a pending hover open.
  useEffect(() => {
    if (!navShown) {
      clearTimers();
      setOpened(undefined);
    }
  }, [navShown]);

  const onLinkAction = (a: MenuAction) => {
    clearTimers();
    setOpened(undefined);
    onMenuAction(a);
  };

  return (
    // box-none: the transparent part of a compact header lets presses through to the page.
    <View testID="site-header-desktop" style={styles.root} pointerEvents="box-none">
      {shownMenu || accountOpen || searchOpen ? (
        // Figma 441:33782 (mega menu), 161:17210 (account) and 161:17195 (search): an open menu dims
        // everything below the header. First child, so the header rows and the panel paint
        // over it. Outside the nav hover region, so a pointer that moves onto it leaves the
        // region and a hover-opened menu closes. Its press closes the menu and takes the click,
        // so nothing under it activates. No fade: Figma draws none and the panel opens without
        // one.
        <Pressable
          ref={backdropRef}
          testID="site-header-mega-menu-backdrop"
          accessible={false}
          focusable={false}
          onPress={() => {
            clearTimers();
            setOpened(undefined);
            setAccountOpen(false);
          }}
          // The account and search panels also open on a compact header, which is shorter than
          // the full one panelMaxHeight subtracts; the window height covers the page in every
          // state. A search panel closes on the backdrop's click (HeaderSearch).
          style={[
            styles.backdrop,
            {height: shownMenu ? panelMaxHeight(windowHeight) : windowHeight},
          ]}
        />
      ) : null}
      <View testID="site-header-top-row" style={[styles.band, styles.row1]}>
        {/* Drawn inside the 56 row, not below it, so the header stays 112 / 56 (gate D2). First
            child, so the search field's panel paints over it (QA 2026-10-21). */}
        <View testID="site-header-top-row-divider" style={styles.divider} pointerEvents="none" />
        <View testID="site-header-top-row-inner" style={styles.inner}>
          <View
            testID="site-header-top-row-left"
            style={[styles.side, tablet && styles.sideTablet]}>
            {giftCardStore ? (
              // R42: HOME is not registered for a gift-card store, so the logo is not a link.
              <View testID="site-header-logo">
                <HeaderLogo height={DESKTOP_LOGO_HEIGHT} />
              </View>
            ) : (
              <Pressable
                testID="site-header-logo"
                onPress={actions.onLogoPress}
                accessibilityRole="link"
                accessibilityLabel="Home">
                <HeaderLogo height={DESKTOP_LOGO_HEIGHT} />
              </Pressable>
            )}
          </View>
          {ecom && showUtilities && showSearch ? (
            // DIS-1907: a type-ahead field; its suggestion panel hangs below it in row 1, which
            // paints over the nav row and the mega menu layer. Focus and an Escape typed in the
            // field close an open mega menu; the Escape never reaches the document listener.
            <HeaderSearch
              testID="site-header-search"
              style={[styles.search, tablet && styles.searchTablet]}
              textStyle={styles.searchText}
              // The mega menu's cap: the panel hangs from row 1, so it is never above the 112.
              panelMaxHeight={panelMaxHeight(windowHeight)}
              onSubmit={actions.onSearchSubmit}
              onResultPress={actions.onSearchResultPress}
              onOpenChange={setSearchOpen}
              backdropRef={backdropRef}
              onFocus={() => {
                clearTimeout(timer.current);
                setOpened(undefined);
              }}
              onEscape={() => {
                clearTimers();
                setOpened(undefined);
              }}
            />
          ) : null}
          <View
            testID="site-header-top-row-right"
            style={[styles.side, styles.right, tablet && styles.rightTablet]}>
            {/* Minimal compact (77:7310) draws the store where the account button was. */}
            {showStoreSelector ? (
              <Animated.View
                testID="site-header-store-slot"
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: space.s8,
                  transform: [
                    {
                      translateX: accountProgress.interpolate({
                        inputRange: [0, 1],
                        outputRange: [showUtilities ? accountSlot(account) : 0, 0],
                      }),
                    },
                  ],
                }}>
                {giftCardStore && actions.onBackToStorePress ? (
                  <BackToStoreButton onPress={actions.onBackToStorePress} />
                ) : null}
                {giftCardStore ? (
                  // R42: MY_STORE is not registered for a gift-card store; plain text, as the
                  // legacy header's "GIFT CARD STORE" title.
                  <View testID="site-header-store" style={styles.store}>
                    <Text style={styles.storeName}>{GIFT_CARD_STORE_COPY}</Text>
                  </View>
                ) : (
                  <Pressable
                    testID="site-header-store"
                    onPress={actions.onStorePress}
                    style={styles.store}
                    accessibilityRole="button"
                    accessibilityLabel={`Store, ${storeName ?? NO_STORE_COPY}`}>
                    <View testID="site-header-store-text" style={styles.storeText}>
                      <Text style={styles.storeName} numberOfLines={1}>
                        {storeName ?? NO_STORE_COPY}
                      </Text>
                      {storeName ? (
                        <Text style={styles.storeAvailability} numberOfLines={1}>
                          {AVAILABILITY_COPY[ecom ? availability : 'inStore']}
                        </Text>
                      ) : null}
                    </View>
                    {/* Figma 104:7822: the 16px stroked chevron, rotated to point right. */}
                    <ChevronRightImg
                      viewBox={`0 0 ${CHEVRON_SIZE} ${CHEVRON_SIZE}`}
                      width={CHEVRON_SIZE}
                      height={CHEVRON_SIZE}
                      color={headerChrome.onGround}
                      style={styles.storeChevron}
                      aria-hidden={true}
                    />
                  </Pressable>
                )}
              </Animated.View>
            ) : null}
            {showUtilities ? (
              // Every state but minimal compact has the account button (475:24517 included).
              <Animated.View
                testID="site-header-account"
                style={[styles.account, {opacity: accountProgress}]}
                {...hiddenWhen(!accountShown)}>
                <AccountMenu
                  {...account}
                  open={accountOpen}
                  onOpenChange={setAccountOpen}
                  backdropRef={backdropRef}
                  // DIS-1937: centred only when the cart chip follows; else it would clip.
                  panelAlign={ecom && showCart ? 'centre' : 'end'}
                />
              </Animated.View>
            ) : null}
            {ecom && showUtilities && showCart ? (
              <Pressable
                testID="site-header-cart"
                onPress={actions.onCartPress}
                style={[styles.chip, styles.cartChip]}
                accessibilityRole="button"
                accessibilityLabel={`Cart, ${cartCount} items, ${cartTotal}`}>
                <View>
                  <MobileCartImg
                    testID="site-header-cart-icon"
                    viewBox={CART_ICON_VIEWBOX}
                    width={CART_ICON_WIDTH}
                    height={CART_ICON_HEIGHT}
                    color={headerChrome.onCartChip}
                    aria-hidden={true}
                  />
                  <View style={styles.badge}>
                    <Text style={styles.badgeText}>{cartCount}</Text>
                  </View>
                </View>
                <Text style={styles.cartTotal}>{cartTotal}</Text>
              </Pressable>
            ) : null}
          </View>
        </View>
      </View>
      {ecom && !menus.error ? (
        // Compact slides the nav row up behind row 1 and fades it; the header box keeps its
        // full height (spec § 7.2). Non-ecommerce stores have no shop menus, and a failed
        // departments load hides the row (spec § 7.3.2, § 7.4).
        <Animated.View
          testID="site-header-nav-row"
          style={{
            opacity: navProgress,
            transform: [
              {
                translateY: navProgress.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-ROW_HEIGHT, 0],
                }),
              },
            ],
          }}
          {...hiddenWhen(!navShown)}>
          {/* C5: the hover region spans the nav row and the open panel, so moving from an item
              into its panel does not close it. Leaving closes only a hover-opened menu.
              A View with pointer events, not a Pressable with onHoverOut: entering a link
              Pressable in the panel locks its ancestors' hover (react-native-web useHover,
              `contain`), which fired onHoverOut and closed the menu 150 ms after the pointer
              reached a link (DIS-1898; RewardsBanner hit the same trap). DOM pointerleave does
              not fire for a move into a descendant. */}
          <View
            testID="site-header-nav-hover-region"
            onPointerEnter={() => clearTimeout(closeTimer.current)}
            onPointerLeave={() => {
              // Leaving drops a pending hover open, then closes a hover-opened menu.
              clearTimers();
              closeTimer.current = setTimeout(
                () => setOpened(current => (current?.by === 'hover' ? undefined : current)),
                HOVER_INTENT_MS,
              );
            }}
            style={styles.band}>
            <View testID="site-header-nav-row-inner" style={[styles.inner, styles.nav]}>
              {navMenus.map(menu => {
                const open = menu.id === openMenu;
                return (
                  <Pressable
                    key={menu.id}
                    ref={node => {
                      if (node) {
                        triggerRefs.current.set(menu.id, node);
                      } else {
                        triggerRefs.current.delete(menu.id);
                      }
                    }}
                    testID={`site-header-menu-trigger-${menu.id}`}
                    onHoverIn={() =>
                      afterIntent(() =>
                        setOpened(current =>
                          current?.id === menu.id ? current : {id: menu.id, by: 'hover'},
                        ),
                      )
                    }
                    onPress={() => {
                      clearTimers();
                      setOpened(current =>
                        current?.id === menu.id && current.by === 'press'
                          ? undefined
                          : {id: menu.id, by: 'press'},
                      );
                    }}
                    style={[styles.navItem, open && styles.navItemOpen]}
                    accessibilityRole="button"
                    accessibilityState={{expanded: open}}>
                    <Text style={[styles.navLabel, open && styles.navLabelOpen]}>{menu.label}</Text>
                  </Pressable>
                );
              })}
            </View>
            {shownMenu ? (
              // box-none: the layer spans the row, so the strips beside the panel pass presses
              // through to the page; only the panel takes them.
              <View
                ref={panelRef}
                testID="site-header-mega-menu-layer"
                style={styles.panelLayer}
                pointerEvents="box-none">
                <MegaMenuPanel
                  menu={shownMenu}
                  loading={menus.isLoading}
                  maxHeight={panelMaxHeight(windowHeight)}
                  onAction={onLinkAction}
                />
              </View>
            ) : null}
          </View>
        </Animated.View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  root: {position: 'relative', zIndex: 10},
  // From the header's bottom edge; the height is the window below the header (panelMaxHeight).
  backdrop: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: universal.black,
    opacity: BACKDROP_OPACITY,
  },
  // Full-width ground and divider; the inner column caps the content (wide 371:33127). Each
  // row paints its own band and the root paints none, so a hidden nav row leaves a
  // transparent band (R40).
  band: {position: 'relative', backgroundColor: headerChrome.ground},
  // Row 1 paints over row 2, so the account panel covers the nav.
  row1: {zIndex: 2},
  divider: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: control.borderWidth,
    backgroundColor: headerChrome.divider,
  },
  inner: {
    width: '100%',
    maxWidth: PAGE_MAX_WIDTH,
    alignSelf: 'center',
    height: ROW_HEIGHT,
    paddingHorizontal: space.s64,
    flexDirection: 'row',
    alignItems: 'center',
  },
  // Equal flex on both sides centres the search field on the page, as Figma draws it.
  // minWidth 'auto' replaces RNW's default 0: a side never narrows below its content, so
  // when the row is short the search shrinks instead of overlapping the store (gate D3).
  side: {flex: 1, minWidth: 'auto', flexDirection: 'row', alignItems: 'center'},
  // Tablet: both sides hug their content (flex 0 would mean basis 0 on web) and the search
  // takes the rest, up to 535. Figma 104:7864 gaps are 46 and 25 around it; 48 and 24 used.
  sideTablet: {flexGrow: 0, flexShrink: 0, flexBasis: 'auto'},
  rightTablet: {flexGrow: 0, flexShrink: 0, flexBasis: 'auto', marginLeft: 'auto'},
  right: {justifyContent: 'flex-end', gap: space.s16},
  search: {
    width: SEARCH_FIELD_WIDTH,
    flexShrink: 1,
    minWidth: 0,
    // A long store name pushes the search left, then shrinks it; the margins keep a gap to
    // the logo and the store instead of letting the field butt against them.
    marginHorizontal: space.s24,
    height: SEARCH_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.s8,
    paddingHorizontal: field.paddingX,
    borderRadius: radius.control,
    backgroundColor: universal.white,
  },
  searchTablet: {
    width: undefined,
    flex: 1,
    maxWidth: SEARCH_FIELD_WIDTH,
    marginLeft: space.s48,
    marginRight: space.s24,
  },
  searchText: {...type.paragraph.p2},
  // Figma 104:7816: chevron is a sibling of the two-line text, centred on the pair. No width
  // cap: the store name and availability show whole, and the search shrinks to make room
  // (`side` minWidth 'auto'). Figma's 169 frame ellipsised "Pick Up & Delivery Available".
  store: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.s4,
    paddingVertical: space.s4,
  },
  // Shrinks inside the capped selector so the one-line texts ellipsise and the chevron stays.
  storeText: {flexShrink: 1, minWidth: 0, gap: space.s4},
  // G7-D3: a web svgr <svg> shrinks by default (flex-shrink: 1); the chevron keeps its 16.
  storeChevron: {flexShrink: 0},
  storeName: {...chromeType.label12Medium, color: headerChrome.onGround},
  storeAvailability: {...chromeType.body12Light, color: headerChrome.onGround},
  // The account panel hangs below row 1 and over the nav row.
  account: {zIndex: 1},
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.s4,
    paddingHorizontal: space.s8,
    paddingVertical: space.s4,
    borderRadius: CHIP_RADIUS,
  },
  // Figma 62:1632: 32 tall. The 20 icon plus the chip's padding would only reach 28.
  cartChip: {height: space.s32, backgroundColor: headerChrome.cartChip},
  // Badge colours follow CartCountBadge's desktop web styles, not Figma (which inherits BM).
  // QA 2026-10-21: on the cart's lower left, as the mobile header (Figma 229:6121).
  badge: {
    position: 'absolute',
    top: BADGE_TOP,
    left: -space.s2,
    height: BADGE_HEIGHT,
    paddingHorizontal: space.s4,
    borderRadius: BADGE_HEIGHT / 2,
    justifyContent: 'center',
    backgroundColor: theme.colour.general.secondary,
  },
  badgeText: {...chromeType.badge9Regular, color: theme.colour.general.primary},
  cartTotal: {...chromeType.label12Medium, color: headerChrome.onCartChip},
  nav: {justifyContent: 'center', gap: NAV_GAP},
  navItem: {paddingHorizontal: space.s12, paddingVertical: space.s8, borderRadius: CHIP_RADIUS},
  navItemOpen: {backgroundColor: universal.white},
  navLabel: {...chromeType.label16Medium, color: headerChrome.onGround},
  navLabelOpen: {color: headerChrome.accent},
  // Hangs below the nav row. 76 = the 64 page inset + 12: the 1288 panel at 1440, and the
  // same inset below it.
  panelLayer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    paddingHorizontal: space.s64 + space.s12,
  },
});
